import { sdk } from '@/services/sdk';
import { committed, todayDate } from '@/shared/data';
import { serviceOfOrder } from '@/shared/creditRates';

const { and, gte, lt, isNull } = window.ApperSDK.CoreSDK;

export const LEDGER_FIELDS = [
  'Name',
  'entry_date_c',
  'kind_c',
  'order_c',
  'service_c',
  'credits_c',
  'cost_c',
  'collected_c',
  'notes_c',
  'CreatedOn',
];

export const LEDGER_KINDS = ['Activation', 'Renewal', 'Expense', 'Other'];

export const LEDGER_KIND_LABELS = {
  Activation: 'New line',
  Renewal: 'Renewal',
  Expense: 'Other cost',
  Other: 'Other',
};

export const LEDGER_KIND_STYLES = {
  Activation: 'border-primary/40 bg-primary/10 text-primary',
  Renewal: 'border-highlight/40 bg-highlight/15 text-highlight',
  Expense: 'border-destructive/40 bg-destructive/10 text-destructive',
  Other: 'border-border bg-muted text-muted-foreground',
};

/** Badge styles keyed by the display label. */
export const LEDGER_LABEL_STYLES = Object.fromEntries(
  LEDGER_KINDS.map((k) => [LEDGER_KIND_LABELS[k], LEDGER_KIND_STYLES[k]]),
);

export const kindLabel = (kind) => LEDGER_KIND_LABELS[kind] ?? kind ?? '';

export const PERIODS = ['month', 'last-month', 'year'];

export const PERIOD_LABELS = {
  month: 'This month',
  'last-month': 'Last month',
  year: 'This year',
};

const pad = (n) => String(n).padStart(2, '0');
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/** [from, to) as YYYY-MM-DD for a period id, or null for all time. */
export function periodRange(period, now = new Date()) {
  const y = now.getFullYear();
  const m = now.getMonth();
  if (period === 'month') return [ymd(new Date(y, m, 1)), ymd(new Date(y, m + 1, 1))];
  if (period === 'last-month') return [ymd(new Date(y, m - 1, 1)), ymd(new Date(y, m, 1))];
  if (period === 'year') return [ymd(new Date(y, 0, 1)), ymd(new Date(y + 1, 0, 1))];
  return null;
}

/** Filter list for a period, ready to spread into and(). */
export function periodFilters(period) {
  const range = periodRange(period);
  if (!range) return [];
  return [gte('entry_date_c', range[0]), lt('entry_date_c', range[1])];
}

/** Entries with no numbers typed in yet. */
export const NEEDS_NUMBERS = () => and(isNull('credits_c'), isNull('cost_c'), isNull('collected_c'));

/** A typed number, or null when blank. */
export function num(v) {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

export const hasNumbers = (e) => num(e?.credits_c) !== null || num(e?.cost_c) !== null || num(e?.collected_c) !== null;

export const profitOf = (e) => (num(e?.collected_c) ?? 0) - (num(e?.cost_c) ?? 0);

/** $12.50, or -$4 for a loss. */
export function signedMoney(n) {
  const v = Math.round(Number(n || 0) * 100) / 100;
  const abs = Math.abs(v);
  const text = Number.isInteger(abs) ? String(abs) : abs.toFixed(2);
  return v < 0 ? `-$${text}` : `$${text}`;
}

export function credits(n) {
  const v = num(n);
  if (v === null) return '—';
  return Number.isInteger(v) ? String(v) : v.toFixed(2);
}

/** The label stored in Name: order code and kind, or the first line of the note. */
export function ledgerName({ kind, orderName, notes }) {
  const label = LEDGER_KIND_LABELS[kind] ?? kind ?? 'Entry';
  if (orderName) return `${orderName} · ${label}`;
  const first = String(notes ?? '').split('\n')[0].trim();
  if (first) return first.length > 60 ? `${first.slice(0, 57)}…` : first;
  return label;
}

/**
 * Open a ledger entry for an order event (activation or renewal).
 * The numbers stay blank until typed in. Returns the new record, or null.
 */
export async function createOrderEntry(order, kind) {
  try {
    const res = await sdk.table('ledger_c').create({
      Name: ledgerName({ kind, orderName: order.Name }),
      entry_date_c: todayDate(),
      kind_c: kind,
      order_c: order.Id,
      ...(serviceOfOrder(order) ? { service_c: serviceOfOrder(order) } : {}),
    });
    return committed(res);
  } catch {
    return null;
  }
}
